import { Alert, Box, Card, CardContent, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { ReactElement } from 'react';
import {
  Create,
  Datagrid,
  DateInput,
  Edit,
  EditButton,
  List,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleList,
  SimpleShowLayout,
  TextInput,
  TopToolbar,
  useRecordContext,
} from 'react-admin';
import type { RaRecord } from 'react-admin';

import { asDisplayString, asIdString } from '@/formatting/text';
import { useBabyScope } from '@/resources/babyScope';
import {
  creatableFields,
  editableFields,
  isBabyScoped,
  listedFields,
  type FieldDescriptor,
  type ResourceDescriptor,
} from '@/resources/descriptors';

import { BabyScopeBar, BabyScopeSelector } from './BabyScopeSelector';
import { ResourceActionButtons } from './ResourceActions';
import { choiceLabel, renderField } from './fields';
import { renderInput } from './inputs';

/**
 * Ecrans generiques construits depuis un `ResourceDescriptor`.
 *
 * Un seul jeu d'ecrans sert les neuf ressources du catalogue. Le gain n'est pas
 * seulement la brievete : c'est qu'une regle — « ne jamais proposer une operation
 * absente », « ne jamais trier une page serveur » — s'applique partout des qu'elle
 * est ecrite une fois ici, au lieu d'etre reoubliee ressource par ressource.
 *
 * Responsive : sous le seuil `sm`, le tableau cede la place a une liste de cartes.
 * Un `Datagrid` a huit colonnes sur un telephone se lit en defilant
 * horizontalement, ce qui n'est pas se lire.
 */

/**
 * Ecran d'attente quand aucun profil n'est choisi.
 *
 * Il porte **le selecteur lui-meme** plutot que de renvoyer vers un autre
 * endroit de l'interface : la consigne « choisissez un profil dans la barre
 * superieure » supposait une barre qui n'existe plus, et un ecran qui demande
 * une action sans l'offrir fait faire un aller-retour pour rien.
 *
 * Le chemin d'API n'y figure plus : `/babies/{babyId}/milk-batches` s'adresse a
 * qui branche l'outil, pas a qui l'utilise.
 */
const BabyScopeRequired = ({ descriptor }: { descriptor: ResourceDescriptor }): ReactElement => (
  <Card sx={{ mt: 2 }}>
    <CardContent>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6">Sélectionnez un profil bébé</Typography>
          <Typography variant="body2" color="text.secondary">
            « {descriptor.label} » est une collection tenue par famille : choisissez le profil dont
            vous voulez voir les données.
          </Typography>
        </Box>
        <BabyScopeSelector autoFocus />
      </Stack>
    </CardContent>
  </Card>
);

/** Bandeau d'explication issu du descripteur. */
const ResourceNotice = ({ descriptor }: { descriptor: ResourceDescriptor }): ReactElement | null =>
  descriptor.notice === undefined ? null : (
    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
      {descriptor.notice}
    </Alert>
  );

/**
 * Filtres de la barre de recherche.
 *
 * Sur une ressource paginee **par le serveur**, seuls les filtres que la route
 * interprete sont proposes : un filtre client sur une seule page filtrerait
 * vingt-cinq lignes sur mille et afficherait un resultat faux.
 *
 * Sur une ressource paginee cote client, la collection entiere est en memoire :
 * tout champ liste peut donc etre filtre sans mentir.
 */
const buildFilters = (descriptor: ResourceDescriptor): ReactElement[] => {
  if (descriptor.pagination === 'server') {
    return descriptor.serverFilters.map((name) => {
      const field = descriptor.fields.find((candidate) => candidate.name === name);

      if (name === 'q') {
        return <TextInput key={name} source="q" label="Recherche" alwaysOn resettable />;
      }

      if (name === 'from') {
        return <DateInput key={name} source="from" label="À partir du" />;
      }

      if (name === 'to') {
        return <DateInput key={name} source="to" label="Jusqu’au" />;
      }

      if (field?.choices !== undefined) {
        return (
          <SelectInput
            key={name}
            source={name}
            label={field.label}
            choices={[...field.choices]}
            alwaysOn
          />
        );
      }

      return <TextInput key={name} source={name} label={field?.label ?? name} resettable />;
    });
  }

  return listedFields(descriptor)
    .filter((field) => field.kind !== 'json' && field.kind !== 'identifier')
    .map((field) =>
      field.choices === undefined ? (
        <TextInput key={field.name} source={field.name} label={field.label} resettable />
      ) : (
        <SelectInput key={field.name} source={field.name} label={field.label} choices={[...field.choices]} />
      ),
    );
};

/** Titre d'une ligne dans la liste compacte mobile. */
const compactTitle = (descriptor: ResourceDescriptor, record: Record<string, unknown>): string => {
  const [first] = listedFields(descriptor);

  if (first === undefined) {
    return asIdString(record.id);
  }

  const value = record[first.name];

  if (first.choices !== undefined) {
    return choiceLabel(first, value);
  }

  return value === undefined || value === null || value === ''
    ? asIdString(record.id)
    : asDisplayString(value);
};

/** Sous-titre : les deux champs listes suivants, concatenes. */
const compactSubtitle = (descriptor: ResourceDescriptor, record: Record<string, unknown>): string =>
  listedFields(descriptor)
    .slice(1, 4)
    .map((field) => {
      const value = field.choices === undefined ? record[field.name] : choiceLabel(field, record[field.name]);

      return value === undefined || value === null || value === ''
        ? null
        : `${field.label} : ${asDisplayString(value)}`;
    })
    .filter((entry): entry is string => entry !== null)
    .join(' · ');

const ListActions = ({ descriptor }: { descriptor: ResourceDescriptor }): ReactElement => (
  <TopToolbar>
    <ResourceActionButtons descriptor={descriptor} variant="list" />
  </TopToolbar>
);

const RowActions = ({ descriptor }: { descriptor: ResourceDescriptor }): ReactElement => {
  const record = useRecordContext<Record<string, unknown>>();

  return (
    <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
      <ShowButton />
      {descriptor.capabilities.edit ? <EditButton /> : null}
      {record === undefined ? null : <ResourceActionButtons descriptor={descriptor} variant="row" />}
    </Stack>
  );
};

export const buildList = (descriptor: ResourceDescriptor): (() => ReactElement) => {
  const ListScreen = (): ReactElement => {
    const { babyId } = useBabyScope();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('sm'));

    if (isBabyScoped(descriptor) && babyId === null) {
      return (
        <>
          <ResourceNotice descriptor={descriptor} />
          <BabyScopeRequired descriptor={descriptor} />
        </>
      );
    }

    // Une pagination serveur sans total ne sait pas trier : les en-tetes ne sont
    // donc pas cliquables, et aucun tri par defaut n'est annonce.
    const sortable = descriptor.pagination === 'client';
    const columns = listedFields(descriptor);

    return (
      <>
        <ResourceNotice descriptor={descriptor} />
        {isBabyScoped(descriptor) ? <BabyScopeBar /> : null}
        <List
          title={descriptor.label}
          filters={buildFilters(descriptor)}
          actions={<ListActions descriptor={descriptor} />}
          perPage={25}
          // Aucun tri par defaut sur une pagination serveur : le declarer
          // laisserait croire que la colonne ordonne la collection entiere.
          {...(sortable ? { sort: { field: columns[0]?.name ?? 'id', order: 'DESC' as const } } : {})}
          empty={false}
        >
          {isCompact ? (
            <SimpleList<RaRecord>
              primaryText={(record) => compactTitle(descriptor, record)}
              secondaryText={(record) => compactSubtitle(descriptor, record)}
              rowClick="show"
            />
          ) : (
            <Datagrid
              // Les actions de masse sont retirees : aucune ressource n'accepte
              // de suppression ni de mise a jour groupee, et un bouton qui echoue
              // systematiquement est pire qu'un bouton absent.
              bulkActionButtons={false}
              rowClick="show"
            >
              {columns.map((field) => renderField(field, sortable))}
              <RowActions descriptor={descriptor} />
            </Datagrid>
          )}
        </List>
      </>
    );
  };

  return ListScreen;
};

export const buildShow = (descriptor: ResourceDescriptor): (() => ReactElement) => {
  const ShowScreen = (): ReactElement => (
    <Show
      title={descriptor.label}
      actions={
        <TopToolbar>
          {descriptor.capabilities.edit ? <EditButton /> : null}
          <ResourceActionButtons descriptor={descriptor} variant="row" />
        </TopToolbar>
      }
    >
      <SimpleShowLayout>
        {descriptor.fields.map((field) => renderField(field, false))}
      </SimpleShowLayout>
    </Show>
  );

  return ShowScreen;
};

const FormFields = ({ fields }: { fields: readonly FieldDescriptor[] }): ReactElement => (
  <>{fields.map((field) => renderInput(field))}</>
);

export const buildCreate = (descriptor: ResourceDescriptor): (() => ReactElement) => {
  const CreateScreen = (): ReactElement => {
    const { babyId } = useBabyScope();

    if (isBabyScoped(descriptor) && babyId === null) {
      return <BabyScopeRequired descriptor={descriptor} />;
    }

    return (
      <>
        <ResourceNotice descriptor={descriptor} />
        {isBabyScoped(descriptor) ? <BabyScopeBar /> : null}
        <Create title={`${descriptor.label} — création`} redirect="list">
          <SimpleForm>
            <FormFields fields={creatableFields(descriptor)} />
          </SimpleForm>
        </Create>
      </>
    );
  };

  return CreateScreen;
};

export const buildEdit = (descriptor: ResourceDescriptor): (() => ReactElement) => {
  const EditScreen = (): ReactElement => (
    <>
      <ResourceNotice descriptor={descriptor} />
      <Edit
        title={`${descriptor.label} — modification`}
        // La mutation envoie l'enregistrement entier, champs en lecture seule
        // compris : React Admin soumet les valeurs initiales du formulaire.
        // `version` y figure, et c'est voulu : c'est la version lue, que le
        // serveur compare a la version courante pour refuser en `409` une
        // modification faite sur un profil qui a change depuis (#571). Le
        // serveur ignore les autres cles qu'il ne connait pas.
        mutationMode="pessimistic"
        redirect="show"
        actions={
          <TopToolbar>
            <ShowButton />
            <ResourceActionButtons descriptor={descriptor} variant="row" />
          </TopToolbar>
        }
      >
        <SimpleForm>
          <FormFields fields={editableFields(descriptor)} />
        </SimpleForm>
      </Edit>
    </>
  );

  return EditScreen;
};
